/**
 * Global app store. Light by design — anything heavy (chart features, route
 * geometry) lives in its own module and is referenced by id here.
 *
 * Slices follow the build-plan milestones so they stay independent:
 *   - ownship           (M2)
 *   - tracks            (M2)
 *   - cursor            (M2 — live position readout)
 *   - activeTool        (M3+ — DR, EP, fix, CTS, VRM, EBL, …)
 *   - chartwork         (M3 — separate store in ./chartwork.ts)
 *   - layerVisibility   (M1 — S-52 display categories + per-layer toggles)
 *   - palette           (M1 — day / dusk / night)
 *   - rotation          (M1 / M2 — north-up + course-up)
 *   - selectedFeature   (M1 — for tap-to-query)
 *   - activeScenario    (M5 / phase 2)
 */
import { create } from 'zustand';
import type { ChartFeature, Scenario } from '@/types';
import type { Track, TrackPoint } from '@/ownship';
import { appendTrackPoint } from '@/ownship';
import { loadJson, saveJson } from './persist';
import type { OrientationMode } from './orientation';
import { CHART_PROVIDERS } from '@/chart-sources/providers';

const CHART_PROVIDER_LS = 'acp.chartProvider.v1';
/**
 * The real-world chart source we open on when there's no saved choice. OpenSeaMap
 * is the sensible default: it's non-fictional and covers the whole world online, so
 * a fresh install pairs it with the phone's real GNSS anywhere, rather than dropping
 * the skipper onto the fictional RYA training cells. Fictional charts are the opt-in.
 */
const DEFAULT_CHART_PROVIDER_ID = 'openseamap';
/**
 * Remember the chosen chart provider across reloads. A saved id is restored as long
 * as it's still a provider we know (a deliberately-chosen training set included);
 * an absent id (fresh install) or a stale one that no longer exists falls back to
 * the real-world default — never silently to the fictional 'vector' cells the way
 * an unguarded getProvider() would.
 */
export function loadChartProviderId(): string {
  try {
    const saved =
      typeof localStorage !== 'undefined' ? localStorage.getItem(CHART_PROVIDER_LS) : null;
    if (saved && CHART_PROVIDERS.some((p) => p.id === saved)) return saved;
    return DEFAULT_CHART_PROVIDER_ID;
  } catch {
    return DEFAULT_CHART_PROVIDER_ID;
  }
}
function saveChartProviderId(id: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(CHART_PROVIDER_LS, id);
  } catch {
    /* ignore */
  }
}

// Display state that must survive an app kill/restart (Android kills aggressively).
const PALETTE_LS = 'acp.palette.v1';
const PALETTE_AUTO_LS = 'acp.paletteAuto.v1';
const SYMBOLSET_LS = 'acp.symbolSet.v1';
const GROUPVIS_LS = 'acp.groupVisibility.v1';
const ORIENTATION_LS = 'acp.orientation.v1';
const DECLUTTER_LEVEL_LS = 'acp.declutterLevel.v1';
/** Load the persisted chart orientation, guarding a corrupted value to north. */
function loadOrientation(): OrientationMode {
  const v = loadJson<string>(ORIENTATION_LS, 'north');
  return v === 'head' || v === 'course' ? v : 'north';
}

export type Palette = 'day' | 'dusk' | 'night';

/** Selectable chart-symbology theme (own renderings of the standard symbols). */
export type SymbolSetId = 'current' | 'np5011' | 'np5012';

export type DisplayCategory = 'base' | 'standard' | 'other';

/**
 * Declutter preset. One picker instead of category toggles, so the skipper isn't
 * left reasoning about display categories on a bouncing deck:
 *   - full:    everything the chart carries.
 *   - medium:  keeps a bit of everything but roughly halves the clutter — depth
 *              contours but not spot soundings, buoys but not their light flares,
 *              and only the significant buoys (see the map layers for the exact
 *              filtering). Sits between full and minimal.
 *   - minimal: the Display Base picture only (depths, land, coastline, principal
 *              buoyage) — the bare safe-navigation set.
 */
export type DeclutterLevel = 'full' | 'medium' | 'minimal';

/** The display-category on/off pattern each declutter level maps to. Base is
 *  always on; the level chooses how much Standard / Other detail rides along. */
export function categoriesForDeclutterLevel(level: DeclutterLevel): Record<DisplayCategory, boolean> {
  if (level === 'minimal') return { base: true, standard: false, other: false };
  if (level === 'medium') return { base: true, standard: true, other: false };
  return { base: true, standard: true, other: true };
}

function loadDeclutterLevel(): DeclutterLevel {
  const v = loadJson<string>(DECLUTTER_LEVEL_LS, 'full');
  return v === 'medium' || v === 'minimal' ? v : 'full';
}

export type ActiveTool =
  | 'none'
  | 'pan'
  | 'measure'
  | 'dr'
  | 'ep'
  | 'fix'
  | 'running_fix'
  | 'cts'
  | 'vrm'
  | 'ebl'
  | 'dtr'
  | 'place_ownship'
  | 'add_waypoint'
  | 'set_drift';

export interface OwnshipState {
  position: [number, number] | null;
  /**
   * True heading, degrees, which is where the BOW points and needs a compass.
   * GNSS does not supply it (that is course over ground), and the device fix
   * path deliberately leaves this alone, so on a real device it stays at 0.
   * Zero is due north, not "unknown", which is why nothing should point a
   * symbol at it without checking there is a heading source first. See
   * ownship/vectorSources.ts and map/ownshipIconHeading.ts.
   */
  heading: number;
  sog: number;
  cog: number;
  stw: number;
  /**
   * Estimated GNSS horizontal position accuracy, metres (the "±X m" the
   * receiver reports). Undefined until the first fix carries it. Set from the
   * feed in GnssIngest, never invented in the app.
   */
  accuracyM?: number;
}

export interface ChartPlotterState {
  ownship: OwnshipState;
  setOwnship: (patch: Partial<OwnshipState>) => void;

  tracks: Record<string, Track>;
  /** True when the next recorded fix should start a fresh track segment. */
  trackBreakPending: boolean;
  appendOwnshipTrack: (point: TrackPoint) => void;
  /** Break the breadcrumb at the next recorded fix (used on a source change). */
  breakOwnshipTrack: () => void;
  clearTracks: () => void;
  setSecondaryTrack: (track: Track | null) => void;

  cursor: [number, number] | null;
  setCursor: (p: [number, number] | null) => void;

  activeTool: ActiveTool;
  setActiveTool: (tool: ActiveTool) => void;

  displayCategories: Record<DisplayCategory, boolean>;
  toggleDisplayCategory: (category: DisplayCategory) => void;
  /** Declutter preset (full / medium / minimal). Drives the display categories
   *  plus the extra medium-level layer filtering. Persisted. */
  declutterLevel: DeclutterLevel;
  setDeclutterLevel: (level: DeclutterLevel) => void;
  groupVisibility: Record<string, boolean>;
  toggleGroup: (key: string) => void;

  /** Which chart PROVIDER is showing: the vector cells, or a raster set. */
  chartProviderId: string;
  setChartProvider: (id: string) => void;
  /** Manual chart pick within the provider set (chart id), or null = auto by view. */
  chartOverrideId: string | null;
  setChartOverride: (id: string | null) => void;

  palette: Palette;
  setPalette: (palette: Palette) => void;
  /** Auto palette: when on, day/dusk/night follows the sun at the boat's
   *  position (see AutoPaletteRunner). A manual palette pick turns this off and
   *  pins the choice. Persisted. */
  paletteAuto: boolean;
  setPaletteAuto: (on: boolean) => void;
  /** Which chart-symbology theme is active (Current / NP5011 paper / NP5012 ECDIS). */
  symbolSet: SymbolSetId;
  setSymbolSet: (symbolSet: SymbolSetId) => void;
  rotation: number;
  setRotation: (rotation: number) => void;
  /** Which way is up on the chart: north-up (fixed), head-up (heading) or
   *  course-up (COG). Persisted; the map bearing follows it. */
  orientation: OrientationMode;
  setOrientation: (mode: OrientationMode) => void;
  /** Bumped on every orientation change so the map re-applies its bearing even
   *  when the mode is unchanged (e.g. re-picking North up after a gesture). */
  orientNonce: number;

  selectedFeature: ChartFeature | null;
  /** Other features at the same position (e.g. a light's sectors), for the info sheet. */
  selectedSiblings: ChartFeature[];
  setSelectedFeature: (f: ChartFeature | null, siblings?: ChartFeature[]) => void;

  splitScreen: boolean;
  setSplitScreen: (on: boolean) => void;

  /** Which side-panel tab is showing in the right-hand drawer. */
  rightPanelTab: 'tools' | 'routes' | 'quality';
  setRightPanelTab: (t: 'tools' | 'routes' | 'quality') => void;

  activeScenario: Scenario | null;
  setActiveScenario: (s: Scenario | null) => void;
}

const PRIMARY_TRACK_ID = 'ownship-primary';

export const useChartPlotterStore = create<ChartPlotterState>((set) => ({
  ownship: { position: null, heading: 0, sog: 0, cog: 0, stw: 0 },
  setOwnship: (patch) => set((s) => ({ ownship: { ...s.ownship, ...patch } })),

  tracks: {
    [PRIMARY_TRACK_ID]: {
      id: PRIMARY_TRACK_ID,
      kind: 'primary',
      points: [],
    },
  },
  trackBreakPending: false,
  appendOwnshipTrack: (point) =>
    set((s) => {
      const t = s.tracks[PRIMARY_TRACK_ID] ?? {
        id: PRIMARY_TRACK_ID,
        kind: 'primary' as const,
        points: [],
      };
      // After a source switch, mark the first fix as a segment break so the line
      // doesn't draw a straight jump from the old position to the new one.
      const pt =
        s.trackBreakPending && t.points.length ? { ...point, gap: true } : point;
      const next = appendTrackPoint(t, pt);
      const appended = next.points.length > t.points.length;
      // Nothing appended (the boat has not moved far enough yet) means nothing
      // changed, so hand back the SAME tracks object. Minting a fresh one made
      // every selector watching tracks fire on every fix, which rebuilt the
      // whole ownship layer including every track point drawn so far. Identity
      // is what the feature cache in ownshipLayer keys on, so this line is what
      // makes that cache do anything at all.
      if (!appended) return {};
      return {
        tracks: { ...s.tracks, [PRIMARY_TRACK_ID]: next },
        trackBreakPending: false,
      };
    }),
  breakOwnshipTrack: () => set({ trackBreakPending: true }),
  clearTracks: () =>
    set({
      tracks: {
        [PRIMARY_TRACK_ID]: { id: PRIMARY_TRACK_ID, kind: 'primary', points: [] },
      },
      // A wipe is a clean restart: drop any pending source-change break so the
      // next fix starts the fresh track normally rather than as a gap.
      trackBreakPending: false,
    }),
  setSecondaryTrack: (track) =>
    set((s) => {
      const next = { ...s.tracks };
      if (track) next[track.id] = track;
      else {
        for (const k of Object.keys(next)) {
          if (k !== PRIMARY_TRACK_ID) delete next[k];
        }
      }
      return { tracks: next };
    }),

  cursor: null,
  setCursor: (cursor) => set({ cursor }),

  activeTool: 'pan',
  setActiveTool: (activeTool) => set({ activeTool }),

  displayCategories: categoriesForDeclutterLevel(loadDeclutterLevel()),
  declutterLevel: loadDeclutterLevel(),
  setDeclutterLevel: (declutterLevel) => {
    saveJson(DECLUTTER_LEVEL_LS, declutterLevel);
    set({ declutterLevel, displayCategories: categoriesForDeclutterLevel(declutterLevel) });
  },
  toggleDisplayCategory: (category) =>
    set((s) => ({
      displayCategories: {
        ...s.displayCategories,
        [category]: !s.displayCategories[category],
      },
    })),
  // CATZOC and the tidal-stream overlay are opt-in, off by default.
  // cables ships OFF: anchoring on one is the hazard the layer exists for, but
  // drawn by default it wallpapers a busy harbour in magenta dashes.
  groupVisibility: loadJson<Record<string, boolean>>(GROUPVIS_LS, {
    catzoc: false,
    'tidal-stream': false,
    cables: false,
  }),
  toggleGroup: (key) =>
    set((s) => {
      const groupVisibility = { ...s.groupVisibility, [key]: !(s.groupVisibility[key] ?? true) };
      saveJson(GROUPVIS_LS, groupVisibility);
      return { groupVisibility };
    }),

  // Chart source: the vector cells by default. Swapping provider clears any
  // manual chart override so the new set auto-selects by view.
  chartProviderId: loadChartProviderId(),
  setChartProvider: (chartProviderId) => {
    saveChartProviderId(chartProviderId);
    set({ chartProviderId, chartOverrideId: null });
  },
  chartOverrideId: null,
  setChartOverride: (chartOverrideId) => set({ chartOverrideId }),

  palette: loadJson<Palette>(PALETTE_LS, 'day'),
  setPalette: (palette) => {
    saveJson(PALETTE_LS, palette);
    set({ palette });
  },
  paletteAuto: loadJson<boolean>(PALETTE_AUTO_LS, false),
  setPaletteAuto: (paletteAuto) => {
    saveJson(PALETTE_AUTO_LS, paletteAuto);
    set({ paletteAuto });
  },
  symbolSet: loadJson<SymbolSetId>(SYMBOLSET_LS, 'current'),
  setSymbolSet: (symbolSet) => {
    saveJson(SYMBOLSET_LS, symbolSet);
    set({ symbolSet });
  },
  rotation: 0,
  setRotation: (rotation) => set({ rotation }),
  orientation: loadOrientation(),
  orientNonce: 0,
  setOrientation: (orientation) => {
    saveJson(ORIENTATION_LS, orientation);
    set((st) => ({ orientation, orientNonce: st.orientNonce + 1 }));
  },

  selectedFeature: null,
  selectedSiblings: [],
  setSelectedFeature: (selectedFeature, selectedSiblings = []) =>
    set({ selectedFeature, selectedSiblings }),

  splitScreen: false,
  setSplitScreen: (splitScreen) => set({ splitScreen }),

  rightPanelTab: 'tools',
  setRightPanelTab: (rightPanelTab) => set({ rightPanelTab }),

  activeScenario: null,
  setActiveScenario: (activeScenario) => set({ activeScenario }),
}));

export { PRIMARY_TRACK_ID };
